import circular from '@/helpers/circular';
import api from '@/store/helpers/api';
import i18n from '@/i18n';

const SUCCESS_SCORE = 12;
const CLUE_PENALTY = 1;
const MAX_CLUES = 9;

function getPoints(state) {
  const clues = state.clues[state.current] || [];
  const points = SUCCESS_SCORE - (clues.length * CLUE_PENALTY);

  return points;
}

const initialState = {
  list: {
    en: {},
    es: {},
  },
  isOver: false,
  current: '',
  score: 0,
  loading: false,
  gameEnded: false,
  currentClues: null,
  clues: {},
  cluesOver: false,
};

const getters = {
  current(state) {
    return state.list[i18n.locale][state.current];
  },
  clues(state) {
    return state.clues[state.current] || [];
  },
  score(state) {
    const total = Object.keys(state.list[i18n.locale]).length || 0;
    return `${state.score}/${total}`;
  },
};

const actions = {
  get({
    state,
    commit,
    dispatch,
    rootState,
  }, { classMode, polling }) {
    if (!polling) {
      commit('LOADSTART');
    }
    let url = '/rondo/remaining';
    if (state.isOver) {
      url = '/rondo';
    }

    if (classMode) {
      url = `/rondo/class-mode/${classMode}`;
    }

    return api.get(url)
      .then((resp) => {
        let data = resp;
        if (rootState.classMode.enabled) {
          data = resp.state;
          // console.log(resp);
          dispatch('classMode/rondoSelectTeam', resp.currentTeam, { root: true });
          dispatch('classMode/setScores', {
            game: 'rondo',
            scores: [
              resp.score_0 || 0,
              resp.score_1 || 0,
              resp.score_2 || 0,
              resp.score_3 || 0,
            ],
          }, { root: true });
        }

        const keys = Object.keys(data);
        if (keys && keys.length === 0) {
          commit('IS_OVER');
          return dispatch('get');
        }

        commit('SET', data);

        let charChanged = !resp.currentChar || resp.currentChar !== state.current;
        if (resp.currentChar) {
          commit('SET_CHAR', resp.currentChar);
          charChanged = resp.currentChar !== state.current;
        }

        if (charChanged) {
          commit('NEXT');
        }
        return data;
      })
      .finally(() => {
        commit('LOADEND');
      });
  },
  answer({
    commit,
    dispatch,
    rootGetters,
    state,
  }, { id, success, classMode }) {
    if (classMode) {
      const team = rootGetters['classMode/rondoCurrentTeam'];
      commit('ANSWER_CLASS', {
        success,
        team,
      });
      return dispatch('classMode/answer', {
        success,
        team,
        game: 'rondo',
        currentChar: state.current,
        gameState: JSON.stringify(state.list),
      }, { root: true });
    }

    if (state.isOver) {
      return false;
    }

    commit('ANSWER', success);

    const points = getPoints(state);
    // Don't show loader, since it's an async request
    return api.post('rondo/answer', {
      points,
      id,
      success,
    });
  },
  next({ commit }) {
    commit('NEXT');
    return true;
  },
  // clue({ commit, dispatch, state }, { classMode }) {
  clue({ commit }) {
    commit('CLUE');
    // if (classMode) {
    //   return dispatch('classMode/rondoSetState', JSON.stringify(state.list), { root: true });
    // }
    return true;
  },
  reset({ commit }) {
    commit('RESET');
    return true;
  },
};

const mutations = {
  LOADSTART(state) {
    state.loading = true;
  },
  LOADEND(state) {
    state.loading = false;
  },
  SET(state, list) {
    state.gameEnded = false;
    state.list = list;
  },
  SET_CHAR(state, current) {
    state.current = current;
  },
  NEXT(state) {
    if (state.gameEnded) {
      return null;
    }

    const abc = Object.keys(state.list[i18n.locale]).sort((a, b) => a.localeCompare(b, 'es'));

    if (!state.current) {
      [state.current] = abc;
      state.currentClues = state.clues[state.current];
      return state.current;
    }
    let next = circular.next(abc, state.current);

    // Skipping answered questions
    while (state.list[i18n.locale][next].answered && next !== state.current) {
      next = circular.next(abc, next);
    }

    if (state.current !== next) {
      state.current = next;
    } else if (state.list[i18n.locale][next].answered) {
      // All questions answered
      state.current = null;
      state.gameEnded = true;
    }
    state.currentClues = state.clues[state.current];
    state.cluesOver = false;

    return state.current;
  },
  ANSWER(state, success) {
    const current = state.list[i18n.locale][state.current];
    current.answered = true;
    current.success = success;
    if (success) {
      state.score += 1;
    }
  },
  ANSWER_CLASS(state, { success, team }) {
    const current = state.list[i18n.locale][state.current];
    if (success) {
      current.answered = true;
      current.team = team;
    }
  },
  CLUE(state) {
    const current = state.list[i18n.locale][state.current];
    if (!current || !current.word) {
      console.warn('[store] Rondo: Unable to get clue');
      return false;
    }

    if (!state.clues[state.current]) {
      state.clues[state.current] = [];
    }

    if (state.clues[state.current].length > MAX_CLUES) {
      console.warn('[store] Rondo: Clue already given');
      return false;
    }

    // Answer unique characters
    let answer = String.prototype.concat(...new Set(current.word.toLowerCase()));
    // Remove current char from answer char list
    answer = answer.replace(state.current, '');
    const rand = Math.floor(Math.random() * answer);

    let clue = answer.charAt(rand).toLowerCase();

    while (state.currentClues
      && state.currentClues.length < answer.length - 1
      && state.currentClues.indexOf(clue) > -1
    ) {
      clue = circular.next(answer, clue);
    }

    // If all characters given as clue, don't add more
    if (state.currentClues && state.currentClues.length >= answer.length - 1) {
      state.cluesOver = true;
      return false;
    }

    state.clues[state.current].push(clue);
    state.currentClues = state.clues[state.current];
    return clue;
  },
  IS_OVER(state) {
    state.isOver = true;
  },
  RESET(state) {
    state.list = {
      en: {},
      es: {},
    };
    state.isOver = false;
    state.current = '';
    state.score = 0;
    state.loading = false;
    state.gameEnded = false;
    state.currentClues = null;
    state.clues = {};
    state.cluesOver = false;
  },
};

// Export store
export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
